import { useContext, useState, useEffect } from 'react';
import { ArrowLeft, Calendar, Target, Users, MapPin, AlertTriangle, FileText, Building2, Download } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import AuthContext from '../../../../../../context/AuthContext';
import { useToast } from '../../../../../../context/ToastContext';
import adminAuditService from '../../../../../../services/audit_services/adminAuditService';
import exportAuditPlanService from '../../../../../../services/audit_services/export_pdf_services/exportAuditPlanService';

export default function AdminAuditPlanDetails() {
  const { id } = useParams();
  const { authTokens } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchPlanDetails();
  }, [id]);

  const fetchPlanDetails = async () => {
    setLoading(true);
    
    const result = await adminAuditService.getAuditPlanDetail(authTokens.access, id);
    
    if (result.success) {
      setPlan(result.data);
    } else {
      showError(result.error || 'Erreur lors du chargement des détails du plan');
    }
    
    setLoading(false);
  };

  const handleExportPDF = async () => {
    setExporting(true);
    const result = await exportAuditPlanService.exportAuditPlanPDF(authTokens.access, id);
    
    if (result.success) {
      showSuccess('Plan d\'audit exporté avec succès');
    } else {
      showError(result.error || 'Erreur lors de l\'exportation du plan');
    }
    
    setExporting(false);
  };

  const handleDownloadAttachment = async (attachmentId, fileName) => {
    try {
      showSuccess(`Téléchargement de ${fileName}...`);
      const result = await adminAuditService.downloadAttachment(authTokens.access, attachmentId);
      
      if (result.success) {
        showSuccess('Fichier téléchargé avec succès');
      } else {
        showError(result.error || 'Erreur lors du téléchargement');
      }
    } catch (error) {
      console.error('Error downloading attachment:', error);
      showError('Erreur lors du téléchargement');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'En attente' },
      in_progress: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'En cours' },
      completed: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Complété' },
      closed: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Fermé' },
      achieved: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Archivé' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getPartyRoleBadge = (role) => {
    const roleConfig = {
      auditor: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Auditeur' },
      auditee: { bg: 'bg-green-100', text: 'text-green-800', label: 'Audité' },
      participant: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Participant' },
      approver: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Approbateur' }
    };

    const config = roleConfig[role] || roleConfig.participant;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getRiskProbabilityBadge = (probability) => {
    const probabilityConfig = {
      critical: { bg: 'bg-red-100', text: 'text-red-800', label: 'Critique' },
      high: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Élevé' },
      medium: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Moyen' },
      low: { bg: 'bg-green-100', text: 'text-green-800', label: 'Faible' }
    };

    const config = probabilityConfig[probability] || probabilityConfig.medium;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des détails du plan...</p>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Plan d'audit non trouvé</p>
        <Link to="/dashboard/admin/audits/plans" className="text-green-600 hover:underline mt-4 inline-block">
          Retour aux plans d'audit
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/dashboard/admin/audits/plans"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{plan.title}</h1>
            <p className="text-sm text-gray-600 mt-1">Détails du plan d'audit</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {getStatusBadge(plan.status)}
          {plan.is_released ? (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
              Libéré
            </span>
          ) : (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
              Non libéré
            </span>
          )}
          {plan.is_published ? (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              Publié
            </span>
          ) : (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
              Non publié
            </span>
          )}
          <button
            onClick={handleExportPDF}
            disabled={exporting}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="h-4 w-4 mr-2" />
            {exporting ? 'Export en cours...' : 'Exporter PDF'}
          </button>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Plan Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations du plan</h3>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Target className="h-4 w-4 mr-2 text-green-600" />
                  Objectifs
                </p>
                <p className="text-sm text-gray-900">{plan.objectives}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Périmètre</p>
                <p className="text-sm text-gray-900">{plan.scope || 'Non défini'}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Référence</p>
                <p className="text-sm text-gray-900">{plan.reference || 'Non défini'}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <MapPin className="h-4 w-4 mr-2 text-green-600" />
                  Localisation
                </p>
                <p className="text-sm text-gray-900">{plan.location || 'Non défini'}</p>
              </div>

              {plan.note && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Note</p>
                  <p className="text-sm text-gray-900">{plan.note}</p>
                </div>
              )}
            </div>
          </div>

          {/* Program Information */}
          {plan.program && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Building2 className="h-5 w-5 mr-2 text-green-600" />
                Programme d'audit
              </h3>
              
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-700">Titre</p>
                  <p className="text-sm text-gray-900 mt-1">{plan.program.title}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Objectif</p>
                  <p className="text-sm text-gray-900 mt-1">{plan.program.objective}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Statut</p>
                  <div className="mt-1">{getStatusBadge(plan.program.status)}</div>
                </div>
                <div className="pt-2">
                  <Link
                    to={`/dashboard/admin/audits/programs/${plan.program.id}`}
                    className="text-sm text-green-600 hover:text-green-700 hover:underline"
                  >
                    Voir les détails du programme →
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Parties Involved */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center mb-4">
              <Users className="h-4 w-4 mr-2 text-green-600" />
              Parties impliquées ({plan.parties ? plan.parties.length : 0})
            </h3>
            <div className="space-y-3">
              {plan.parties && plan.parties.length > 0 ? (
                plan.parties.map((party) => (
                  <div key={party.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-gray-600">
                          {party.name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{party.name}</p>
                        <p className="text-xs text-gray-500">{party.email}</p>
                      </div>
                    </div>
                    <div>
                      {getPartyRoleBadge(party.partyRole)}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500">Aucune partie assignée</p>
              )}
            </div>
          </div>

          {/* Attachments */}
          {plan.attachments && plan.attachments.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center mb-4">
                <FileText className="h-4 w-4 mr-2 text-green-600" />
                Pièces jointes ({plan.attachments.length})
              </h3>
              <div className="space-y-3">
                {plan.attachments.map((attachment) => (
                  <div key={attachment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center space-x-3">
                      <FileText className="h-5 w-5 text-green-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{attachment.file_name}</p>
                        <p className="text-xs text-gray-500">
                          {attachment.file_size && `${(attachment.file_size / 1024).toFixed(2)} KB • `}
                          Téléchargé le {new Date(attachment.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <button 
                      onClick={() => handleDownloadAttachment(attachment.id, attachment.file_name)}
                      className="inline-flex items-center px-3 py-1 text-sm text-green-600 hover:text-green-800 border border-green-300 rounded hover:bg-green-50 transition-colors"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Télécharger
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Métadonnées</h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-600">Créé par</p>
                <p className="text-sm text-gray-900 mt-1">{plan.created_by?.full_name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Date de création</p>
                <p className="text-sm text-gray-900 mt-1">
                  {new Date(plan.created_at).toLocaleDateString('fr-FR')}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Dernière mise à jour</p>
                <p className="text-sm text-gray-900 mt-1">
                  {new Date(plan.updated_at).toLocaleDateString('fr-FR')}
                </p>
              </div>
              {plan.last_updated_by && (
                <div>
                  <p className="text-xs text-gray-600">Dernière modification par</p>
                  <p className="text-sm text-gray-900 mt-1">{plan.last_updated_by.full_name}</p>
                </div>
              )}
              {plan.release_date && (
                <div>
                  <p className="text-xs text-gray-600">Date de publication</p>
                  <p className="text-sm text-gray-900 mt-1">
                    {new Date(plan.release_date).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              )}
              {plan.publish_date && (
                <div>
                  <p className="text-xs text-gray-600">Date de mise en ligne</p>
                  <p className="text-sm text-gray-900 mt-1">
                    {new Date(plan.publish_date).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Risks Section */}
      {plan.risks && plan.risks.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <AlertTriangle className="h-5 w-5 mr-2 text-orange-600" />
            Risques associés ({plan.risks.length})
          </h3>
          
          <div className="space-y-4">
            {plan.risks.map((risk) => (
              <div key={risk.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="text-base font-semibold text-gray-900">{risk.risk_name}</h4>
                    <p className="text-sm text-gray-600 mt-1">{risk.description}</p>
                  </div>
                  <div className="ml-4">
                    {getRiskProbabilityBadge(risk.probability)}
                  </div>
                </div>

                {risk.consequence && (
                  <div className="mb-3">
                    <p className="text-xs font-medium text-gray-700">Conséquence</p>
                    <p className="text-sm text-gray-900 mt-1">{risk.consequence}</p>
                  </div>
                )}

                {/* Risk Actions */}
                {risk.actions && risk.actions.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs font-medium text-gray-700 mb-2">
                      Actions de contrôle ({risk.actions.length})
                    </p>
                    <div className="space-y-2">
                      {risk.actions.map((action) => (
                        <div key={action.id} className="flex items-start space-x-3 p-2 bg-gray-50 rounded">
                          <div className={`mt-0.5 w-4 h-4 rounded-full flex items-center justify-center ${
                            action.is_completed ? 'bg-green-500' : 'bg-gray-300'
                          }`}>
                            {action.is_completed && (
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm text-gray-900">{action.action}</p>
                            <div className="flex items-center space-x-3 mt-1">
                              {action.user_assigned_name && (
                                <span className="text-xs text-gray-500">
                                  Assigné à: {action.user_assigned_name}
                                </span>
                              )}
                              {action.due_date && (
                                <span className="text-xs text-gray-500">
                                  Échéance: {new Date(action.due_date).toLocaleDateString('fr-FR')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* View Audit Sections Link */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <FileText className="h-5 w-5 mr-2 text-green-600" />
              Sections d'audit
            </h3>
            <p className="text-sm text-gray-600 mt-1">Consultez les sections d'audit de ce plan</p>
          </div>
          <Link
            to={`/dashboard/admin/audits/plans/${plan.id}/sections`}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <FileText className="h-4 w-4 mr-2" />
            Voir les sections
          </Link>
        </div>
      </div>
    </div>
  );
}
